<script lang="ts">
  import { queryFlow } from '../lib/analytics/flow';
  import Chart from '../lib/charts/Chart.svelte';
  import ChartCard from '../lib/charts/ChartCard.svelte';
  import { dimensionColor } from '../lib/charts/dimension-colors';
  import { toCompositionOption, toTimeChartOption, type ChartMode } from '../lib/charts/options';
  import type { DataStore } from '../lib/data/store';
  import type { FlowQuery, SeriesResult } from '../lib/data/types';
  import FilterBar from '../lib/filters/FilterBar.svelte';
  import { formatCents, signedClass } from '../lib/presentation/format';
  import { previousDatePeriod } from '../lib/presentation/period';
  import { labelFlowSeries } from '../lib/presentation/series';
  import type { AppState } from '../lib/state/app.svelte';
  import KpiCard from '../lib/ui/KpiCard.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';

  let {
    store,
    appState,
    kind,
  }: { store: DataStore; appState: AppState; kind: 'expense' | 'income' } = $props();
  let breakdown = $state<FlowQuery['breakdown']>('category');
  let chartMode = $state<ChartMode>('bar');
  let movingWindow = $state(0);
  let title = $derived(kind === 'expense' ? 'Spese' : 'Entrate');
  let singular = $derived(kind === 'expense' ? 'spesa' : 'entrata');

  function filters() {
    return {
      ...(appState.accountFilterActive ? { accountIds: new Set(appState.accountIds) } : {}),
      ...(appState.categoryFilterActive
        ? {
            categoryIds: new Set(appState.categoryIds),
            includeUncategorized: appState.includeUncategorized,
          }
        : {}),
    };
  }

  function makeQuery(
    grain: FlowQuery['grain'],
    requestedBreakdown: FlowQuery['breakdown'],
    metric: FlowQuery['metric'] = kind,
    applyMovingAverage = false,
  ): FlowQuery {
    return {
      metric,
      grain,
      breakdown: requestedBreakdown,
      filters: filters(),
      visiblePeriod: {
        from: appState.fromDate ?? store.core.calendar[0]!.date,
        to: appState.toDate ?? store.core.calendar.at(-1)!.date,
      },
      ...(applyMovingAverage && movingWindow > 0
        ? { movingAverage: { window: movingWindow, mode: 'trailing' as const } }
        : {}),
    };
  }

  function cumulative(result: SeriesResult): SeriesResult {
    return {
      ...result,
      series: result.series.map((series) => {
        let sum = 0;
        return { ...series, values: series.values.map((value) => (sum += value)) };
      }),
    };
  }

  let monthlyQuery = $derived(makeQuery('month', breakdown, kind, true));
  let monthly = $derived(
    labelFlowSeries(
      queryFlow(store.transactions, store.core.calendar, monthlyQuery),
      monthlyQuery,
      store,
    ),
  );
  let totalQuery = $derived(makeQuery('month', 'none'));
  let totalSeries = $derived(
    labelFlowSeries(
      queryFlow(store.transactions, store.core.calendar, totalQuery),
      totalQuery,
      store,
    ),
  );
  let countQuery = $derived(makeQuery('month', 'none', 'count'));
  let countResult = $derived(queryFlow(store.transactions, store.core.calendar, countQuery));
  let dailyQuery = $derived(makeQuery('day', 'none'));
  let daily = $derived(
    labelFlowSeries(
      queryFlow(store.transactions, store.core.calendar, dailyQuery),
      dailyQuery,
      store,
    ),
  );
  let cumulativeResult = $derived(cumulative(totalSeries));
  let total = $derived(totalSeries.total ?? 0);
  let count = $derived(countResult.total ?? 0);
  let nonEmptyMonths = $derived(Math.max(1, totalSeries.buckets.length));
  let days = $derived(Math.max(1, daily.buckets.length));
  let previousTotal = $derived.by(() => {
    const query = makeQuery('month', 'none');
    query.visiblePeriod = previousDatePeriod(query.visiblePeriod);
    return queryFlow(store.transactions, store.core.calendar, query).total ?? 0;
  });
  let comparison = $derived(
    previousTotal === 0 ? null : (total - previousTotal) / Math.abs(previousTotal),
  );
  let distribution = $derived.by(() =>
    monthly.series
      .map((series) => ({
        name: series.label,
        id: series.id,
        colorKey: series.colorKey ?? `series:${series.id}`,
        value: series.values.reduce((sum, value) => sum + value, 0),
      }))
      .sort((a, b) => b.value - a.value),
  );
  let fastest = $derived.by(() =>
    monthly.series
      .map((series) => ({
        label: series.label,
        colorKey: series.colorKey ?? `series:${series.id}`,
        change: (series.values.at(-1) ?? 0) - (series.values.at(-2) ?? 0),
        value: series.values.at(-1) ?? 0,
      }))
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
      .slice(0, 5),
  );

  function switchFlow(next: 'expense' | 'income'): void {
    appState.navigate(next === 'expense' ? 'expenses' : 'income');
  }

  function drillDown(categoryId: string): void {
    if (breakdown === 'category') {
      appState.categoryIds = categoryId === '__uncategorized__' ? [] : [categoryId];
      appState.includeUncategorized = categoryId === '__uncategorized__';
      appState.categoryFilterActive = true;
    }
    appState.navigate('transactions');
  }
</script>

<main id="main-content" class="page">
  <PageHeader
    {title}
    description={`Analizza ${kind === 'expense' ? 'quanto spendi' : 'da dove arrivano le entrate'}, individua variazioni e raggiungi ogni movimento sottostante.`}
    state={appState}
  />
  <div class="flow-switch segmented" aria-label="Tipo di flusso">
    <button class:active={kind === 'expense'} type="button" onclick={() => switchFlow('expense')}
      >Spese</button
    >
    <button class:active={kind === 'income'} type="button" onclick={() => switchFlow('income')}
      >Entrate</button
    >
  </div>
  <FilterBar state={appState} />

  <div class="kpi-grid">
    <KpiCard
      hero
      label={`${title} nel periodo`}
      value={formatCents(total)}
      detail={`${count} movimenti`}
      tone={kind === 'expense' ? 'negative' : 'positive'}
    />
    <KpiCard
      label="Media mensile"
      value={formatCents(total / nonEmptyMonths)}
      detail={`${nonEmptyMonths} mesi`}
    />
    <KpiCard
      label="Media giornaliera"
      value={formatCents(total / days)}
      detail={`${days} giorni di calendario`}
    />
    <KpiCard
      label={`Importo medio per ${singular}`}
      value={formatCents(count ? total / count : 0)}
      detail={comparison === null
        ? 'Confronto non disponibile'
        : `${new Intl.NumberFormat('it-IT', { style: 'percent', signDisplay: 'exceptZero', maximumFractionDigits: 1 }).format(comparison)} sul periodo precedente`}
      tone={comparison === null
        ? 'neutral'
        : signedClass(kind === 'expense' ? -comparison : comparison)}
    />
  </div>

  <div class="page-grid">
    <div class="span-7">
      <ChartCard
        title={`${title} cumulative`}
        description="Il cumulativo riparte dall'inizio del periodo selezionato."
        result={cumulativeResult}
        option={toTimeChartOption(cumulativeResult, 'area')}
        height="21rem"
      />
    </div>
    <section class="card span-5">
      <div class="card-header">
        <div>
          <h2>Distribuzione</h2>
          <p class="muted">
            Totale per {breakdown === 'category'
              ? 'categoria'
              : breakdown === 'account'
                ? 'conto'
                : 'gruppo'}.
          </p>
        </div>
      </div>
      <Chart
        option={toCompositionOption(distribution.slice(0, 10), 'donut')}
        ariaLabel={`Distribuzione ${title.toLocaleLowerCase('it')}`}
        height="21rem"
      />
    </section>

    <section class="card span-12">
      <div class="card-header">
        <div>
          <h2>Andamento mensile</h2>
          <p class="muted">Serie dense: i mesi senza movimenti restano a zero.</p>
        </div>
        <div class="chart-controls">
          <label
            >Media <select bind:value={movingWindow}
              ><option value={0}>Nessuna</option><option value={3}>3 periodi</option><option
                value={6}>6 periodi</option
              ></select
            ></label
          >
          <div class="segmented" aria-label="Suddivisione">
            {#each [['category', 'Categorie'], ['account', 'Conti'], ['group', 'Gruppi'], ['none', 'Totale']] as item}
              <button
                class:active={breakdown === item[0]}
                type="button"
                onclick={() => (breakdown = item[0] as FlowQuery['breakdown'])}>{item[1]}</button
              >
            {/each}
          </div>
          <div class="segmented" aria-label="Tipo grafico">
            {#each [['bar', 'Colonne'], ['line', 'Linea'], ['area', 'Area']] as item}
              <button
                class:active={chartMode === item[0]}
                type="button"
                onclick={() => (chartMode = item[0] as ChartMode)}>{item[1]}</button
              >
            {/each}
          </div>
        </div>
      </div>
      <Chart
        option={toTimeChartOption(monthly, chartMode)}
        ariaLabel={`Andamento mensile ${title.toLocaleLowerCase('it')}`}
        height="23rem"
        onSelect={(seriesId) => drillDown(seriesId)}
      />
    </section>

    <div class="span-8">
      <ChartCard
        title={`${title} giornaliere`}
        description="Gli eventi isolati restano visibili; passa alla tabella per il dettaglio."
        result={daily}
        option={toTimeChartOption(daily, 'bar')}
        height="20rem"
      />
    </div>
    <section class="card list-card span-4">
      <header class="list-header">
        <h2>Variazioni principali</h2>
        <p class="muted">Differenza rispetto al mese precedente</p>
      </header>
      <ul class="composition-list">
        {#each fastest as item}
          <li
            class="dimension-row"
            style={`--dimension-color: ${dimensionColor(item.colorKey, item.label)}`}
          >
            <span class="dimension-label">{item.label}</span><strong
              class={signedClass(kind === 'expense' ? -item.change : item.change)}
              >{formatCents(item.change)}</strong
            ><small>{formatCents(item.value)} nel mese</small>
          </li>
        {:else}<li><span>Nessun dato disponibile</span></li>{/each}
      </ul>
      {#if breakdown === 'category'}
        <div style="padding: .6rem 1rem">
          <button
            class="text-button"
            type="button"
            onclick={() => distribution[0] && drillDown(distribution[0].id)}
            >Apri i movimenti della prima categoria</button
          >
        </div>
      {/if}
    </section>
  </div>
</main>
